/**
 * Safe color utilities that wrap polished functions with whitespace trimming
 * and error handling to prevent crashes from malformed color strings.
 *
 * Always use these instead of importing directly from 'polished'.
 */
import {
  rgba as polishedRgba,
  darken as polishedDarken,
  lighten as polishedLighten,
  transparentize as polishedTransparentize,
  getLuminance as polishedGetLuminance,
  linearGradient as polishedLinearGradient,
  desaturate as polishedDesaturate,
  parseToRgb as polishedParseToRgb,
} from 'polished';

/**
 * Sanitize a color string by trimming whitespace.
 * Returns empty string for null/undefined, original value if not a string.
 */
export const sanitizeColor = color => {
  if (color == null) {
    return '';
  }
  if (typeof color === 'string') {
    return color.trim();
  }

  return color;
};

/**
 * Safe wrapper for polished rgba.
 * Trims whitespace from color string before processing.
 */
export const rgba = (color, alpha) => {
  try {
    return polishedRgba(sanitizeColor(color), alpha);
  } catch {
    // Return a fallback transparent color on error
    return `rgba(0,0,0,${alpha ?? 1})`;
  }
};

/**
 * Safe wrapper for polished darken.
 * Trims whitespace from color string before processing.
 */
export const darken = (amount, color) => {
  try {
    return polishedDarken(amount, sanitizeColor(color));
  } catch {
    return color;
  }
};

/**
 * Safe wrapper for polished lighten.
 * Trims whitespace from color string before processing.
 */
export const lighten = (amount, color) => {
  try {
    return polishedLighten(amount, sanitizeColor(color));
  } catch {
    return color;
  }
};

/**
 * Safe wrapper for polished transparentize.
 * Trims whitespace from color string before processing.
 */
export const transparentize = (amount, color) => {
  try {
    return polishedTransparentize(amount, sanitizeColor(color));
  } catch {
    return color;
  }
};

/**
 * Safe wrapper for polished getLuminance.
 * Trims whitespace from color string before processing.
 * Returns 0.5 (mid-gray luminance) on error.
 */
export const getLuminance = color => {
  try {
    return polishedGetLuminance(sanitizeColor(color));
  } catch {
    return 0.5;
  }
};

/**
 * Safe wrapper for polished linearGradient.
 * Trims whitespace from all color strings in colorStops.
 */
export const linearGradient = ({colorStops, fallback, toDirection}) => {
  try {
    const sanitizedColorStops = colorStops?.map?.(stop => {
      if (typeof stop === 'string') {
        return sanitizeColor(stop);
      }
      if (Array.isArray(stop)) {
        return [sanitizeColor(stop[0]), stop[1]];
      }

      return stop;
    });

    return polishedLinearGradient({
      colorStops: sanitizedColorStops,
      fallback: sanitizeColor(fallback),
      toDirection,
    });
  } catch {
    return {background: fallback || 'transparent'};
  }
};

/**
 * Safe wrapper for polished desaturate.
 * Trims whitespace from color string before processing.
 */
export const desaturate = (amount, color) => {
  try {
    return polishedDesaturate(amount, sanitizeColor(color));
  } catch {
    return color;
  }
};

/**
 * Safe wrapper for polished parseToRgb.
 * Trims whitespace from color string before processing.
 * Returns a default black RGB object on error.
 */
export const parseToRgb = color => {
  try {
    return polishedParseToRgb(sanitizeColor(color));
  } catch {
    return {red: 0, green: 0, blue: 0};
  }
};
